import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GraphResolver } from '../services/graphResolver';
import { useIdentity } from '../hooks/useIdentity';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { ErrorDisplay } from './ErrorDisplay';
import { getStoredRelays } from '../utils/relay';
import {
  getMessageByHash,
  getSignatures,
  getKeysInWindow,
} from '../utils/relay';
import {
  verifyMessageHash,
  verifyMessageSignatures,
} from '../utils/verification';
import {
  getValidateursIfMessageAValider,
  validateMessageAValiderForSync,
} from '../utils/validatorsAccept';
import { tryDecryptWithKeys } from '../services/syncDecrypt';
import { updateGraphFromMessage } from '../services/syncUpdateGraph';
import type { MsgChiffre, MsgSignature, MsgCle } from '../types/message';
import type { MessageBase } from '../types/message';

interface DiagnosticResult {
  hash: string;
  messageFound: boolean;
  messageDecrypted: boolean;
  messageValidated: boolean;
  hashValid: boolean;
  signaturesFound: number;
  signaturesValid: number;
  keysFound: number;
  validatorsChecked: boolean;
  error?: string;
  messageType?: string;
  serviceUuid?: string[];
  hashMismatch?: boolean;
  signatureErrors?: string[];
}

export function DiagnosticScreen(): JSX.Element {
  const navigate = useNavigate();
  const { identity } = useIdentity();
  const { error, handleError, clearError } = useErrorHandler();
  const [hashInput, setHashInput] = useState('');
  const [isDiagnosing, setIsDiagnosing] = useState(false);
  const [result, setResult] = useState<DiagnosticResult | null>(null);

  const handleDiagnose = async (): Promise<void> => {
    if (hashInput.trim() === '') {
      handleError('Hash requis', 'MISSING_HASH');
      return;
    }

    setIsDiagnosing(true);
    clearError();
    setResult(null);

    try {
      const hash = hashInput.trim();
      const relays = getStoredRelays().filter((r) => r.enabled);
      if (relays.length === 0) {
        handleError('Aucun relais activé', 'NO_ENABLED_RELAYS');
        return;
      }

      const diagnostic: DiagnosticResult = {
        hash,
        messageFound: false,
        messageDecrypted: false,
        messageValidated: false,
        hashValid: false,
        signaturesFound: 0,
        signaturesValid: 0,
        keysFound: 0,
        validatorsChecked: false,
      };

      // Fetch message by hash
      let msgChiffre: MsgChiffre | null = null;
      for (const relay of relays) {
        try {
          msgChiffre = await getMessageByHash(relay.endpoint, hash);
          diagnostic.messageFound = true;
          break;
        } catch {
          continue;
        }
      }

      if (msgChiffre === null) {
        diagnostic.error = 'Message non trouvé sur les relais';
        setResult(diagnostic);
        return;
      }

      // Fetch signatures
      const allSignatures: MsgSignature[] = [];
      for (const relay of relays) {
        try {
          const sigs = await getSignatures(relay.endpoint, hash);
          allSignatures.push(...sigs);
        } catch {
          continue;
        }
      }
      diagnostic.signaturesFound = allSignatures.length;

      // Fetch keys
      const allKeys: MsgCle[] = [];
      const now = Date.now();
      const start = now - 86400000; // 24h
      for (const relay of relays) {
        try {
          const keys = await getKeysInWindow(relay.endpoint, start, now);
          const relevantKeys = keys.filter((k) => k.hash_message === hash);
          allKeys.push(...relevantKeys);
        } catch {
          continue;
        }
      }
      diagnostic.keysFound = allKeys.length;

      // Try to decrypt
      if (identity !== null && allKeys.length > 0) {
        const decrypted = await tryDecryptWithKeys(
          msgChiffre,
          allKeys,
          identity,
        );
        if (decrypted !== null && typeof decrypted === 'object') {
          const msg = decrypted as {
            types?: { types_names_chiffres?: string };
            datajson?: { services_uuid?: string[] };
            hash?: { hash_value: string };
            timestamp: number;
          };
          diagnostic.messageDecrypted = true;
          diagnostic.messageType = msg.types?.types_names_chiffres ?? 'unknown';
          diagnostic.serviceUuid = msg.datajson?.services_uuid;

          const msgBase = decrypted as MessageBase;

          // Verify hash
          diagnostic.hashValid = await verifyMessageHash(msgBase);
          if (!diagnostic.hashValid) {
            diagnostic.hashMismatch = true;
            diagnostic.error = 'Hash recalculé ≠ hash déclaré';
          }

          // Verify signatures
          const signatureErrors: string[] = [];
          if (allSignatures.length > 0) {
            const sigList = allSignatures.map((s) => s.signature);
            const { valid, invalid } = verifyMessageSignatures(msgBase, sigList);
            diagnostic.signaturesValid = valid.length;

            for (const inv of invalid) {
              signatureErrors.push(
                `Signature invalide (clé: ${inv.cle_publique.slice(0, 16)}...)`,
              );
            }

            // Check validators if MessageAValider
            const validateurs = getValidateursIfMessageAValider(msgBase);
            if (validateurs !== null) {
              diagnostic.validatorsChecked = true;
              const isValid = validateMessageAValiderForSync(
                msgBase,
                sigList,
                validateurs,
                '[Diagnostic]',
              );
              diagnostic.messageValidated = isValid;
              if (!isValid) {
                signatureErrors.push('Signatures ne satisfont pas les validateurs');
              }
            } else {
              diagnostic.messageValidated = valid.length > 0;
            }
          } else {
            signatureErrors.push('Aucune signature trouvée');
          }

          if (signatureErrors.length > 0) {
            diagnostic.signatureErrors = signatureErrors;
          }

          // Update graph if valid
          if (diagnostic.hashValid && diagnostic.messageValidated) {
            const graphResolver = new GraphResolver();
            updateGraphFromMessage(decrypted, graphResolver);
          }
        }
      }

      setResult(diagnostic);
    } catch (err) {
      handleError(err, 'Erreur lors du diagnostic');
    } finally {
      setIsDiagnosing(false);
    }
  };

  return (
    <main>
      <h1>Diagnostic hash / objet</h1>
      {error !== null && <ErrorDisplay error={error} onDismiss={clearError} />}
      <section aria-labelledby="hash-input">
        <h2 id="hash-input">Hash à diagnostiquer</h2>
        <div>
          <label htmlFor="hash-input-field">
            Hash
            <input
              id="hash-input-field"
              type="text"
              value={hashInput}
              onChange={(e) => {
                setHashInput(e.target.value);
              }}
              placeholder="hash-value"
            />
          </label>
          <button
            type="button"
            onClick={handleDiagnose}
            disabled={isDiagnosing || hashInput.trim() === ''}
          >
            {isDiagnosing ? 'Diagnostic en cours...' : 'Diagnostiquer'}
          </button>
        </div>
      </section>

      {result !== null && (
        <section aria-labelledby="diagnostic-result">
          <h2 id="diagnostic-result">Résultat du diagnostic</h2>
          <div>
            <p>
              <strong>Hash:</strong> {result.hash}
            </p>
            <p>
              <strong>Message trouvé:</strong> {result.messageFound ? '✓ Oui' : '✗ Non'}
            </p>
            {result.messageFound && (
              <>
                <p>
                  <strong>Message déchiffré:</strong>{' '}
                  {result.messageDecrypted ? '✓ Oui' : '✗ Non'}
                </p>
                {result.messageDecrypted && (
                  <>
                    <p>
                      <strong>Type:</strong> {result.messageType ?? 'N/A'}
                    </p>
                    {result.serviceUuid !== undefined && (
                      <p>
                        <strong>Service UUID:</strong> {result.serviceUuid.join(', ')}
                      </p>
                    )}
                    <p>
                      <strong>Hash valide:</strong>{' '}
                      {result.hashValid ? '✓ Oui' : '✗ Non'}
                    </p>
                    {result.hashMismatch === true && (
                      <p style={{ color: 'red' }}>
                        <strong>Erreur:</strong> Hash recalculé ≠ hash déclaré
                      </p>
                    )}
                    <p>
                      <strong>Validateurs vérifiés:</strong>{' '}
                      {result.validatorsChecked ? '✓ Oui' : '—'}
                    </p>
                    <p>
                      <strong>Message validé:</strong>{' '}
                      {result.messageValidated ? '✓ Oui' : '✗ Non'}
                    </p>
                  </>
                )}
                <p>
                  <strong>Signatures trouvées:</strong> {result.signaturesFound}
                </p>
                <p>
                  <strong>Signatures valides:</strong> {result.signaturesValid}
                </p>
                {result.signatureErrors !== undefined &&
                  result.signatureErrors.length > 0 && (
                    <div>
                      <strong>Erreurs de signature:</strong>
                      <ul>
                        {result.signatureErrors.map((err, idx) => (
                          <li key={idx} style={{ color: 'red' }}>
                            {err}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                <p>
                  <strong>Clés trouvées:</strong> {result.keysFound}
                </p>
                {result.keysFound === 0 && result.messageFound && (
                  <p style={{ color: 'orange' }}>
                    <strong>Attention:</strong> Aucune clé trouvée - message
                    indéchiffrable
                  </p>
                )}
              </>
            )}
            {result.error !== undefined && (
              <p>
                <strong>Erreur:</strong> {result.error}
              </p>
            )}
          </div>
        </section>
      )}

      <div>
        <button
          type="button"
          onClick={(): void => {
            navigate('/');
          }}
        >
          Retour
        </button>
      </div>
    </main>
  );
}
